/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd. All rights reserved.
This file contains Protected Intellectual Property owned by Kaleida Ltd and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (explicitly not developed by Kaleida), whether or not such application forms part of the Platform.
For the avoidance of doubt, the inclusion of any application within the Platform does not of itself grant any right to use Protected Intellectual Property in that application unless that application was developed by Kaleida.
*/

// IP-NOTICE: PROTECTED
import { Component, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Constants } from 'app/core/models/constants';
import { AsideService } from 'app/layout/components/aside-container/aside.service';
import { ConfirmationModalComponent } from 'app/shared/components/confirmation-modal/confirmation-modal.component';
import { InternalFileUploaderComponent } from 'app/shared/components/file-uploader/file-uploader.component';
import { ReactiveFormHelperService } from 'app/shared/helpers/reactive-form-helper.service';
import { CookieService } from 'app/shared/services/cookie.service';
import { SnackBarService } from 'app/shared/services/snack-bar.service';
import { environment } from 'environments/environment';
import { debounceTime, startWith, switchMap } from 'rxjs/operators';
import { ExternalRaiseDefectModel } from './models/external-raise-defect.model';
import { ExternalRaiseDefectService } from './services/external-raise-defect.service';

@Component({
    selector: 'external-raise-defect-form',
    templateUrl: './external-raise-defect-form.component.html',
    standalone: false
})
export class ExternalRaiseDefectFormComponent implements OnInit {
  public data: any;
  uploadEndpoint = '';
  baseEndpointOverride = '';
  allowedTypes = [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'image/png',
    'image/jpeg',
    'application/pdf',
    'message/rfc822',
    'application/vnd.ms-outlook',
    'text/plain',
  ];

  softwareFeatureIdLookupData: any[];

  viewModel: ExternalRaiseDefectModel;

  dataForm: FormGroup;
  tinyMceSettings = Constants.EditorSettings.FullSettings;

  @ViewChild(InternalFileUploaderComponent, { static: false }) fileUploaderComponent: InternalFileUploaderComponent;

  // shortcuts to controls for readability
  get description(): AbstractControl {
    return this.dataForm.controls['description'];
  }
  get softwareFeatureId(): AbstractControl {
    return this.dataForm.controls['softwareFeatureId'];
  }
  get details(): AbstractControl {
    return this.dataForm.controls['details'];
  }
  get reproductionSteps(): AbstractControl {
    return this.dataForm.controls['reproductionSteps'];
  }

  constructor(
    private fb: FormBuilder,
    private service: ExternalRaiseDefectService,
    private asideService: AsideService,
    private router: Router,
    private cookieService: CookieService,
    private snackBar: SnackBarService,
    private formHelperService: ReactiveFormHelperService,
    private dialog: MatDialog
  ) {
    this.dataForm = fb.group({
      description: ['', [Validators.required, Validators.maxLength(200)]],
      softwareFeatureId: ['', [Validators.required]],
      details: ['', [Validators.required]],
      reproductionSteps: [''],
    });
  }

  ngOnInit() {
    this.loadFormDetails();
    this.uploadEndpoint = this.service.getCreateWithAttachmentsEndpoint();
    this.baseEndpointOverride = `${environment.baseUrlForRaisingDefect}${environment.apiPrefixForRaisingDefect}`;
  }

  private uploadDocument(updateModel) {
    this.fileUploaderComponent.headers.push({ name: 'X-JsonData', value: JSON.stringify(updateModel) });
    this.fileUploaderComponent.onApply();
  }

  fileUploadComplete(): void {
    this.fileUploaderComponent.clearQueue();
    this.asideService.close('external-defect-raised');
    this.snackBar.info('The Defect has been submitted to Kaleida');
  }

  loadFormDetails() {
    this.service.getLookupDataForDefect().then((response: ExternalRaiseDefectModel) => {
      this.viewModel = response;
      this.initialiseForm();
    });
  }
  initialiseForm() {
    this.service.softwareFeatureIdLookupData = this.viewModel.softwareFeatureIdLookupData;

    this.dataForm.controls['softwareFeatureId'].valueChanges
      .pipe(
        debounceTime(500), // waits 500ms before calling
        startWith(''),
        switchMap((value) => this.service.getSoftwareFeatureLookupList(value)) // pass the control value to the service method
      )
      .subscribe((results) => {
        (this.softwareFeatureIdLookupData = results.items.map((item) => {
          return {
            id: item.data,
            text: item.name,
          };
        })).unshift({ id: 0, text: 'No Associated Feature' });
      }); // create lookup data from the api response

    this.dataForm.markAsPristine();
  }

  private createUpdateModel() {
    return {
      description: this.description.value,
      featureKey: this.softwareFeatureId.value.id,
      details: this.details.value,
      reproductionSteps: this.reproductionSteps.value,
      buildNumber: environment.buildLabel,
      language: window.navigator.language,
      userAgent: window.navigator.userAgent,
      route: window.location.href,
      userId: this.cookieService.getLoggedInUserId(),
      username: this.cookieService.getLoggedInFullName(),
    };
  }

  save() {
    if (this.formHelperService.isFormValid(this.dataForm)) {
      const updateModel = this.createUpdateModel();
      if (this.fileUploaderComponent.uploader.queue.length > 0) {
        this.uploadDocument(updateModel);
      } else {
        this.service.addNew(updateModel).then(() => {
          this.asideService.close('external-defect-raised');
          this.snackBar.info('The Defect has been submitted to Kaleida');
        });
      }
    }
  }

  cancel() {
    if (this.formHelperService.isFormDirty(this.dataForm) || this.fileUploaderComponent.uploader.queue.length > 0) {
      const dialogRef = this.dialog.open(ConfirmationModalComponent, {
        width: '450px',
        data: {
          title: 'Confirm Lost Changes',
          message: 'Changes have been made. Closing this form will lose these changes. Are you sure you want to continue?',
        },
      });

      dialogRef.componentInstance.onClose.subscribe((result) => {
        if (result.result) {
          this.asideService.close('cancel');
        }
      });
    } else {
      this.asideService.close('cancel');
    }
  }

  // for autocomplete so that it knows which property ro display
  displaySelectedItem(option?: any) {
    return option ? option.text : undefined;
  }
}
